import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit, inject } from '@angular/core';
import { Tile, TileWidget } from '../../types/tile.types';
import { timeZoneLabel } from './home-widget.util';

type ClockWidget = Extract<TileWidget, { type: 'clock' }>;

/** Uhr-Kachel, digital oder analog, optional mit Datum und in einer anderen Zeitzone. */
@Component({
  selector: 'app-clock-tile',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./home-widget.scss'],
  template: `
    <div class="widget-card clock-card" [class.clock-analog]="clock.style === 'analog'" [style.--tile-accent]="tile.color" role="timer" [attr.aria-label]="tile.name + ': ' + timeText + (clock.showDate ? ', ' + dateText : '')">
      <div class="widget-head">
        <i class="ri-time-line" aria-hidden="true"></i>
        <span class="widget-title">{{ tile.name }}</span>
      </div>
      <div class="widget-body" aria-hidden="true">
        @if (clock.style === 'analog') {
        <svg class="clock-face" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="47" class="clock-rim" />
          @for (tick of ticks; track tick) {
          <line x1="50" [attr.y1]="tick % 5 === 0 ? 7 : 6" x2="50" [attr.y2]="tick % 5 === 0 ? 14 : 9" class="clock-tick" [class.clock-tick-hour]="tick % 5 === 0" [attr.transform]="'rotate(' + tick * 6 + ' 50 50)'" />
          }
          <line x1="50" y1="56" x2="50" y2="27" class="clock-hand clock-hand-hour" [attr.transform]="'rotate(' + hourAngle + ' 50 50)'" />
          <line x1="50" y1="58" x2="50" y2="15" class="clock-hand clock-hand-minute" [attr.transform]="'rotate(' + minuteAngle + ' 50 50)'" />
          @if (clock.showSeconds) {
          <line x1="50" y1="60" x2="50" y2="12" class="clock-hand clock-hand-second" [attr.transform]="'rotate(' + secondAngle + ' 50 50)'" />
          }
          <circle cx="50" cy="50" r="2.5" class="clock-center" />
        </svg>
        } @else {
        <div class="clock-digital">{{ timeText }}</div>
        }
        @if (clock.showDate) { <div class="clock-date">{{ dateText }}</div> }
        @if (clock.timeZone) { <div class="widget-meta">{{ zoneLabel }}</div> }
      </div>
    </div>
  `,
})
export class ClockTileComponent implements OnInit, OnDestroy {
  @Input({ required: true }) public tile!: Tile;
  public readonly ticks = Array.from({ length: 60 }, (_, i) => i);
  public timeText = '';
  public dateText = '';
  public hourAngle = 0;
  public minuteAngle = 0;
  public secondAngle = 0;
  private timer?: ReturnType<typeof setInterval>;
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);

  public get clock(): ClockWidget {
    return this.tile.widget as ClockWidget;
  }

  public get zoneLabel(): string {
    return timeZoneLabel(this.clock.timeZone);
  }

  public ngOnInit(): void {
    this.tick();
    // Außerhalb der Zone, damit nicht jede Sekunde die ganze Seite geprüft wird.
    this.zone.runOutsideAngular(() => {
      this.timer = setInterval(() => {
        this.tick();
        this.cdr.detectChanges();
      }, 1000);
    });
  }

  public ngOnDestroy(): void {
    clearInterval(this.timer);
  }

  private tick(): void {
    const clock = this.clock;
    const timeZone = clock.timeZone || undefined;
    const now = new Date();
    const parts = Object.fromEntries(new Intl.DateTimeFormat('de-DE', { timeZone, hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' })
      .formatToParts(now).map(part => [part.type, Number(part.value)]));
    const hours = parts['hour'] ?? 0;
    const minutes = parts['minute'] ?? 0;
    const seconds = parts['second'] ?? 0;
    this.hourAngle = (hours % 12) * 30 + minutes * 0.5;
    this.minuteAngle = minutes * 6 + seconds * 0.1;
    this.secondAngle = seconds * 6;
    this.timeText = new Intl.DateTimeFormat('de-DE', { timeZone, hour: '2-digit', minute: '2-digit', ...(clock.showSeconds ? { second: '2-digit' } : {}) }).format(now);
    this.dateText = new Intl.DateTimeFormat('de-DE', { timeZone, weekday: 'long', day: 'numeric', month: 'long' }).format(now);
  }
}
